import { useTranslation } from 'react-i18next'
import type { ContentLocale } from '@/data/public-catalog'
import { collectionSystemProducts, type CollectionSystemProductKey } from '@/data/product-pages/collection-systems'
import { ProductMedia } from './ProductMedia'

function AccessoriBody({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const text = (key: string, options?: Record<string, unknown>) => t(`productPage.accessori.${key}`, { lng: 'it', ...options })
  const families = text('families', { returnObjects: true }) as unknown as Array<{ title: string; description: string }>
  return <section className="shell-container product-section collection-body accessori-body" aria-labelledby="accessori-body-title" lang={language === 'en' ? 'it' : undefined}>
    <div className="product-copy accessori-copy">
      <h2 id="accessori-body-title">{text('bodyTitle')}</h2>
      <p>{text('intro')}</p>
    </div>
    <div className="accessori-range">
      {families.map(family => <section key={family.title}><h3>{family.title}</h3><p>{family.description}</p></section>)}
    </div>
  </section>
}

function EsalivaBody({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const paragraphs = t('productPage.esaliva.paragraphs', { returnObjects: true }) as string[]
  const components = t('productPage.esaliva.components', { returnObjects: true }) as Array<{ title: string; description: string }>
  return <section className="shell-container product-section collection-body esaliva-body" aria-labelledby="esaliva-body-title" lang={language}>
    <div className="product-copy esaliva-copy">
      <h2 id="esaliva-body-title">{t('productPage.esaliva.bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <section className="esaliva-kit" aria-labelledby="esaliva-kit-title">
      <h2 id="esaliva-kit-title">{t('productPage.esaliva.kitTitle')}</h2>
      <ol>{components.map(component => <li key={component.title}><h3>{component.title}</h3><p>{component.description}</p></li>)}</ol>
    </section>
  </section>
}

function ProvetteBody({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const text = (key: string) => t(`productPage.provette.${key}`, { lng: 'it' })
  const media = collectionSystemProducts['prd-provette-bd'].supportingMedia
  return <section className="shell-container product-section collection-body provette-body" aria-labelledby="provette-body-title" lang={language === 'en' ? 'it' : undefined}>
    <div className="provette-layout">
      <div className="product-copy provette-copy">
        <h2 id="provette-body-title">{text('bodyTitle')}</h2>
        <p>{text('system')}</p>
        <p>{text('tubes')}</p>
      </div>
      <div className="provette-media">
        <ProductMedia path={media[0]} altKey="productPage.provette.logoAlt" className="provette-logo" />
        <ProductMedia path={media[1]} altKey="productPage.provette.tubesAlt" className="provette-tubes" />
      </div>
    </div>
  </section>
}

function X1Body({ language }: { language: ContentLocale }) {
  const { t } = useTranslation('common')
  const media = collectionSystemProducts['prd-x1-pro-se-semen-quality-analyzer'].supportingMedia
  return <section className="shell-container product-section collection-body x1-body" aria-labelledby="x1-body-title">
    <div className="x1-heading">
      <h2 id="x1-body-title" lang="en">{t('productPage.x1.bodyTitle', { lng: 'en' })}</h2>
      <p lang={language}>{t('productPage.x1.sourceNote', { lng: language })}</p>
    </div>
    <div className="x1-plates">
      {media.map((path, index) => <section key={path} aria-labelledby={`x1-plate-${index + 1}`}>
        <h3 id={`x1-plate-${index + 1}`}>{t('productPage.x1.plateTitle', { lng: language, index: index + 1 })}</h3>
        <ProductMedia path={path} altKey={`productPage.x1.plateAlt${index + 1}`} className="x1-plate" />
      </section>)}
    </div>
  </section>
}

export function CollectionSystemBody({ productKey, language }: { productKey: CollectionSystemProductKey; language: ContentLocale }) {
  if (productKey === 'accessori') return <AccessoriBody language={language} />
  if (productKey === 'esaliva') return <EsalivaBody language={language} />
  if (productKey === 'provette') return <ProvetteBody language={language} />
  return <X1Body language={language} />
}
